import React from "react";
import {
  Button,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Dropdown,
  Input,
  UncontrolledDropdown,
} from "reactstrap";
import { Link } from "react-router-dom";
import IntlMessages from "../../../helpers/IntlMessages";
import TemplateWarningCard from "../../../components/common/TemplateWarningCard";

const NUMBER_FORMATTER = new Intl.NumberFormat("vi-VN", {
  maximumFractionDigits: 2,
});

const STATUS_OPTIONS = [
  { key: "", label: "Tất cả" },
  { key: "low_stock", label: "Sắp hết" },
  { key: "in_stock", label: "Còn hàng" },
  { key: "out_of_stock", label: "Hết hàng" },
  { key: "no_cost", label: "Chưa giá vốn" },
];

const STATUS_META = {
  in_stock: {
    label: "Tồn kho ổn định",
    className: "is-normal",
  },
  low_stock: {
    label: "Sắp hết hàng",
    className: "is-warning",
  },
  out_of_stock: {
    label: "Hết hàng",
    className: "is-danger",
  },
  no_cost: {
    label: "Chưa nhập giá vốn",
    className: "is-neutral",
  },
};

const QUICK_ACTIONS = [
  {
    key: "warehouse_import",
    to: "/app/pages/warehouse-import",
    title: "Nhập kho",
    subtitle: "Tạo phiếu nhập",
    icon: "simple-icon-login",
    className: "is-primary",
  },
  {
    key: "warehouse_export",
    to: "/app/pages/warehouse-export",
    title: "Xuất kho",
    subtitle: "Tạo phiếu xuất",
    icon: "simple-icon-logout",
    className: "is-danger",
  },
  {
    key: "warehouse_transfer",
    to: "/app/pages/warehouse-transfer",
    title: "Chuyển kho",
    subtitle: "Giữa các kho",
    icon: "simple-icon-refresh",
    className: "is-warning",
  },
  {
    key: "inventory_audit",
    to: "/app/pages/inventory-audit",
    title: "Kiểm kê",
    subtitle: "Kiểm tra đối chiếu",
    icon: "simple-icon-check",
    className: "is-success",
  },
];

const formatNumber = (value, fallback = "0") => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? NUMBER_FORMATTER.format(numericValue)
    : fallback;
};

const formatCurrency = (value) => `${formatNumber(Math.round(Number(value) || 0))} đ`;

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const DEFAULT_LOW_STOCK_THRESHOLD = 10;

const resolveItemStatusKey = (item) => {
  const count = Number(item?.count || 0);
  if (count <= 0) return "out_of_stock";

  const minThreshold = Number(item?.min_stock_threshold || 0);
  const isLowStock =
    minThreshold > 0
      ? count <= minThreshold
      : count <= DEFAULT_LOW_STOCK_THRESHOLD;

  if (isLowStock) return "low_stock";
  if (item?.status_key === "no_cost" || Number(item?.price_new || 0) <= 0) {
    return "no_cost";
  }
  return item?.status_key || "in_stock";
};

const resolveItemStatus = (item) =>
  STATUS_META[resolveItemStatusKey(item)] || STATUS_META.in_stock;

const resolveItemMeta = (item) => {
  const values = [item?.sku, item?.warehouse_name, item?.storage_location]
    .map((value) => (value === null || value === undefined ? "" : String(value).trim()))
    .filter(Boolean);
  return values.length ? values.join(" · ") : "Chưa có thông tin kho";
};

const StockDashboardMobile = ({
  filters,
  filterOptions,
  onFilterChange,
  onApplyFilters,
  onResetFilters,
  onSearch,
  data = [],
  allData = [],
  showTemplate,
  selectedItems = [],
  onToggleItem,
  onOpenAdd,
  onOpenUpdate,
  onOpenUpdateCount,
  onDeleteItem,
  onDeleteSelected,
  dashboardData,
  isLoadingDashboard,
  canCreate,
  canUpdate,
  canDelete,
}) => {
  const [sortKey, setSortKey] = React.useState("name-asc");
  const [showFilters, setShowFilters] = React.useState(false);
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [mobileVisibleCount, setMobileVisibleCount] = React.useState(20);
  const [mobileSearch, setMobileSearch] = React.useState(filters.search || "");
  const [openMenuMobileId, setOpenMenuMobileId] = React.useState(null);
  const summary = dashboardData?.summary || {};
  const warehouseNames = filterOptions?.warehouse_names || [];
  const categoryNames = filterOptions?.category_names || [];
  const activeFilterCount = [
    filters?.category_name,
    filters?.warehouse_name,
    filters?.status,
  ].filter(Boolean).length;

  React.useEffect(() => {
    if (!selectedItems.length) setSelectionMode(false);
  }, [selectedItems.length]);

  React.useEffect(() => {
    setMobileSearch(filters.search || "");
  }, [filters.search]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (mobileSearch !== (filters.search || "") && onSearch) {
        onSearch(mobileSearch);
      }
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [filters.search, mobileSearch, onSearch]);

  React.useEffect(() => {
    setMobileVisibleCount(20);
  }, [
    filters.category_name,
    filters.status,
    filters.warehouse_name,
    mobileSearch,
    allData.length,
  ]);

  const filteredData = React.useMemo(() => {
    const source = allData.length ? allData : data;
    const keyword = normalizeText(mobileSearch);
    return source.filter((item) => {
      if (
        filters.category_name &&
        item.category_name !== filters.category_name
      ) {
        return false;
      }
      if (
        filters.warehouse_name &&
        item.warehouse_name !== filters.warehouse_name
      ) {
        return false;
      }
      if (filters.status && resolveItemStatusKey(item) !== filters.status) {
        return false;
      }
      if (keyword) {
        const haystack = normalizeText(
          `${item.name} ${item.sku} ${item.barcode} ${item.category_name} ${item.warehouse_name}`,
        );
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [allData, data, filters, mobileSearch]);

  const sortedData = React.useMemo(() => {
    const nextData = [...filteredData];
    if (sortKey === "name-desc") {
      return nextData.sort((a, b) =>
        String(b?.name || "").localeCompare(String(a?.name || ""), "vi"),
      );
    }
    if (sortKey === "qty-asc") {
      return nextData.sort((a, b) => Number(a?.count || 0) - Number(b?.count || 0));
    }
    if (sortKey === "qty-desc") {
      return nextData.sort((a, b) => Number(b?.count || 0) - Number(a?.count || 0));
    }
    return nextData.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "vi"),
    );
  }, [filteredData, sortKey]);
  const mobileVisibleItems = sortedData.slice(0, mobileVisibleCount);

  const handleStatusChange = (status) => {
    onFilterChange("status", status);
  };

  const hasTemplateStocks =
    (Array.isArray(allData) && allData.some((s) => Boolean(s?.isTemplate))) ||
    (Array.isArray(data) && data.some((s) => Boolean(s?.isTemplate)));

  return (
    <div className="stock-dashboard-mobile d-md-none">
      <div className="product-modern-mobile-header">
        <div>
          <h1>Tồn kho</h1>
        </div>
        <Button
          color="primary"
          disabled={!canCreate}
          onClick={onOpenAdd}
          style={{ "marginRight": "0.15rem" }}
        >
          <span>Thêm tồn kho</span>
        </Button>
{/*         
        {canCreate && (
          <Button className="stock-mobile-fab" color="primary" onClick={onOpenAdd}>
            <i className="simple-icon-plus" />
            Thêm nguyên liệu mới
          </Button>
        )} */}
      </div>

      {hasTemplateStocks ? (
        <div className="px-3 mb-2">
          <TemplateWarningCard compact />
        </div>
      ) : null}

      <form
        className="stock-mobile-search"
        onSubmit={(event) => {
          event.preventDefault();
          if (onSearch) onSearch(mobileSearch);
          else onApplyFilters();
        }}
      >
        <i className="simple-icon-magnifier stock-mobile-search-icon" />
        <Input
          type="search"
          value={mobileSearch}
          onChange={(event) => setMobileSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && onSearch) {
              event.preventDefault();
              onSearch(event.currentTarget.value);
            }
          }}
          placeholder="Tìm theo tên, SKU, barcode"
          aria-label="Tìm kiếm tồn kho"
        />
        {mobileSearch ? (
          <Button
            type="button"
            color="link"
            aria-label="Xóa tìm kiếm"
            onClick={() => {
              setMobileSearch("");
              if (onSearch) onSearch("");
            }}
          >
            <i className="simple-icon-close" />
          </Button>
        ) : null}
      </form>

      <div className="stock-mobile-stats">
        <Card className="stock-mobile-stat-card is-primary">
          <CardBody>
            <div className="stock-mobile-stat-label">Giá trị tồn kho</div>
            <div className="stock-mobile-stat-value">
              {isLoadingDashboard
                ? "..."
                : formatCurrency(summary.total_stock_value || 0)}
            </div>
            <div className="stock-mobile-stat-sub">
              {formatNumber(summary.total_items || 0)} SKU
            </div>
          </CardBody>
        </Card>
        <Card className="stock-mobile-stat-card">
          <CardBody>
            <div className="stock-mobile-stat-label">Cần nhập hàng</div>
            <div className="stock-mobile-stat-value is-danger">
              {isLoadingDashboard
                ? "..."
                : formatNumber(
                    Number(summary.low_stock_count || 0) +
                      Number(summary.out_of_stock_count || 0),
                  )}
            </div>
            <div className="stock-mobile-stat-sub">Sắp hết hoặc hết hàng</div>
          </CardBody>
        </Card>
      </div>

      {warehouseNames.length >= 2 && (
        <div className="stock-mobile-chip-scroll">
          <button
            type="button"
            className={`stock-mobile-chip ${
              !filters.warehouse_name ? "is-active" : ""
            }`}
            onClick={() => onFilterChange("warehouse_name", "")}
          >
            Tất cả kho
          </button>
          {warehouseNames.map((warehouseName) => (
            <button
              type="button"
              className={`stock-mobile-chip ${
                filters.warehouse_name === warehouseName ? "is-active" : ""
              }`}
              key={warehouseName}
              onClick={() => onFilterChange("warehouse_name", warehouseName)}
            >
              {warehouseName}
            </button>
          ))}
        </div>
      )}

      <div className="stock-mobile-quick-grid">
        {QUICK_ACTIONS.map((action) => (
          <Link
            key={action.key}
            to={action.to}
            className="stock-mobile-quick-action"
          >
            <span
              className={`stock-mobile-quick-icon ${action.className}`}
              aria-hidden="true"
            >
              <i className={action.icon} />
            </span>
            <span>
              <strong>{action.title}</strong>
              <small>{action.subtitle}</small>
            </span>
          </Link>
        ))}
      </div>

      <div className="stock-mobile-chip-scroll stock-mobile-status-chips">
        {STATUS_OPTIONS.map((status) => (
          <button
            type="button"
            className={`stock-mobile-chip ${
              (filters.status || "") === status.key ? "is-active" : ""
            }`}
            key={status.key || "all"}
            onClick={() => handleStatusChange(status.key)}
          >
            {status.label}
          </button>
        ))}
      </div>

      <div className="stock-mobile-list-heading">
        {/* <div>
          <h2>Danh sách tồn kho</h2>
          <small>
            {formatNumber(filteredData.length)} nguyên liệu
          </small>
        </div> */}
        <div className="stock-mobile-heading-actions">
          <Button
            color="link"
            className={`stock-mobile-filter-button ${
              showFilters ? "is-active" : ""
            }`}
            onClick={() => setShowFilters((value) => !value)}
          >
            <i className="simple-icon-equalizer mr-1" />
            Lọc
            {activeFilterCount > 0 && (
              <span className="stock-mobile-filter-count">{activeFilterCount}</span>
            )}
          </Button>
          <UncontrolledDropdown>
            <DropdownToggle color="link" className="stock-mobile-sort-button">
              {sortKey === "name-asc"
                ? "A-Z"
                : sortKey === "name-desc"
                  ? "Z-A"
                  : sortKey === "qty-asc"
                    ? "SL tăng"
                    : "SL giảm"}
              <i className="simple-icon-arrow-down ml-1" />
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={() => setSortKey("name-asc")}>
                Tên A-Z
              </DropdownItem>
              <DropdownItem onClick={() => setSortKey("name-desc")}>
                Tên Z-A
              </DropdownItem>
              <DropdownItem onClick={() => setSortKey("qty-asc")}>
                Số lượng tăng dần
              </DropdownItem>
              <DropdownItem onClick={() => setSortKey("qty-desc")}>
                Số lượng giảm dần
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <div className="product-modern-mobile-list-toolbar stock-mobile-product-toolbar">
          <button
            type="button"
            className={`product-modern-mobile-select-all ${
              selectionMode ? "active" : ""
            }`}
            onClick={() => setSelectionMode((value) => !value)}
          >
            {selectionMode ? "Xong" : "Chọn"}
          </button>
          {/* <UncontrolledDropdown>
            <DropdownToggle color="link" aria-label="Tác vụ danh sách">
              <i className="simple-icon-options-vertical" />
            </DropdownToggle>
            <DropdownMenu right>
              {canCreate && (
                <DropdownItem onClick={onOpenImport}>
                  <i className="simple-icon-cloud-upload mr-2" />
                  Nhập kho bằng Excel
                </DropdownItem>
              )}
              <DropdownItem onClick={onExport}>
                <i className="simple-icon-cloud-download mr-2" />
                Xuất Excel
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown> */}
        </div>
      </div>

      {showFilters && (
        <Card className="stock-mobile-filter-panel">
          <CardBody>
            <label htmlFor="stock-mobile-category">Danh mục</label>
            <Input
              id="stock-mobile-category"
              type="select"
              value={filters.category_name || ""}
              onChange={(event) =>
                onFilterChange("category_name", event.target.value)
              }
            >
              <option value="">Tất cả danh mục</option>
              {categoryNames.map((categoryName) => (
                <option key={categoryName} value={categoryName}>
                  {categoryName}
                </option>
              ))}
            </Input>
            <div className="stock-mobile-filter-actions">
              <Button
                color="link"
                disabled={!activeFilterCount}
                onClick={onResetFilters}
              >
                Bỏ lọc
              </Button>
              <Button color="primary" onClick={() => setShowFilters(false)}>
                Xong
              </Button>
            </div>
          </CardBody>
        </Card>
      )}


      <div className="product-modern-mobile-list">
        {!mobileVisibleItems.length ? (
          <div className="product-modern-mobile-empty">
            <i className="iconsminds-box-full" />
            <strong>Không tìm thấy nguyên liệu</strong>
            <span>Hãy thử thay đổi từ khóa hoặc bộ lọc.</span>
          </div>
        ) : (
          mobileVisibleItems.map((item) => {
            const isInactive =
              item?.is_active === false || item?.is_active === "false";
            const isMenuOpen = openMenuMobileId === item.id;
            const status = isInactive
              ? { label: "Ngừng sử dụng", className: "is-neutral" }
              : resolveItemStatus(item);
            const inventoryValue =
              Number(item.count || 0) *
              Number(item.avarage_price || item.price_new || 0);
            return (
              <div
                key={item.id}
                className={`product-modern-mobile-item ${
                  selectedItems.includes(item.id)
                    ? "product-modern-mobile-item-selected"
                    : ""
                } ${isInactive ? "product-modern-mobile-item-muted" : ""} ${isMenuOpen ? "menu-open" : ""}`}
                style={
                  isMenuOpen
                    ? {
                        position: "relative",
                        zIndex: 1050,
                        opacity: 1,
                      }
                    : isInactive
                      ? {
                          opacity: 0.55,
                          backgroundColor: "#f8fafc",
                          transition: "opacity 0.2s ease",
                        }
                      : undefined
                }
              >
                <div
                  className="product-modern-mobile-item-main"
                  role={canUpdate ? "button" : undefined}
                  tabIndex={canUpdate ? 0 : undefined}
                  onClick={() => canUpdate && onOpenUpdate(item)}
                  onKeyDown={(event) => {
                    if (
                      canUpdate &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      onOpenUpdate(item);
                    }
                  }}
                >
                  {selectionMode ? (
                    <Input
                      type="checkbox"
                      className="product-modern-mobile-checkbox"
                      checked={selectedItems.includes(item.id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => onToggleItem(item.id, event)}
                      aria-label={`Chọn ${item.name || "nguyên liệu"}`}
                    />
                  ) : null}

                  <div className="product-modern-thumb stock-mobile-product-thumb">
                    <i className="simple-icon-layers" />
                  </div>

                  <div className="product-modern-mobile-info">
                    <div className="product-modern-mobile-name-row">
                      <strong>{item.name || "Nguyên liệu chưa đặt tên"}</strong>
                      {isInactive ? (
                        <span
                          className="badge badge-light text-muted border ml-1"
                          style={{ fontSize: "0.65rem" }}
                        >
                          Ngừng sử dụng
                        </span>
                      ) : null}
                      {item.isTemplate ? (
                        <span className="badge badge-info ml-1">
                          <IntlMessages id="template.badge" />
                        </span>
                      ) : null}
                    </div>
                    <div className="product-modern-mobile-meta">
                      {resolveItemMeta(item)}
                    </div>
                    <div
                      className={`product-modern-mobile-stock ${
                        resolveItemStatusKey(item) === "low_stock"
                          ? "is-low"
                          : resolveItemStatusKey(item) === "out_of_stock"
                            ? "is-out"
                            : resolveItemStatusKey(item) === "no_cost"
                              ? "is-hidden"
                              : ""
                      }`}
                    >
                      {status.label} · {formatNumber(item.count || 0)}
                      {item.unit ? ` ${item.unit}` : ""}
                    </div>
                  </div>

                  <div className="product-modern-mobile-price stock-mobile-product-value">
                    <strong>{formatCurrency(inventoryValue)}</strong>
                    <small>Giá trị tồn</small>
                  </div>

                  <div
                    className="product-modern-mobile-item-actions"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <Dropdown
                      isOpen={openMenuMobileId === item.id}
                      toggle={() =>
                        setOpenMenuMobileId((prev) =>
                          prev === item.id ? null : item.id,
                        )
                      }
                    >
                      <DropdownToggle
                        color="link"
                        className="stock-mobile-product-menu"
                        aria-label="Thao tác sản phẩm"
                      >
                        <i className="simple-icon-options-vertical" />
                      </DropdownToggle>
                      <DropdownMenu right positionFixed>
                        {canUpdate && (
                          <DropdownItem onClick={() => onOpenUpdate(item)}>
                            Sửa tồn kho
                          </DropdownItem>
                        )}
                        {canUpdate && (
                          <DropdownItem onClick={() => onOpenUpdateCount(item)}>
                            Nhập tồn kho
                          </DropdownItem>
                        )}
                        {canDelete && (
                          <DropdownItem
                            className="text-danger"
                            onClick={() => onDeleteItem(item)}
                          >
                            Xóa
                          </DropdownItem>
                        )}
                        {!canUpdate && !canDelete && (
                          <DropdownItem disabled>Không có thao tác</DropdownItem>
                        )}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {mobileVisibleItems.length < sortedData.length ? (
        <button
          type="button"
          className="product-modern-mobile-load-more"
          onClick={() => setMobileVisibleCount((count) => count + 20)}
        >
          Xem thêm
          <span>
            {formatNumber(mobileVisibleItems.length)}/
            {formatNumber(sortedData.length)}
          </span>
        </button>
      ) : null}

      {selectionMode && selectedItems.length > 0 && canDelete && (
        <div className="product-modern-mobile-bulk-bar stock-mobile-product-bulk">
          <strong>{selectedItems.length} đã chọn</strong>
          <button type="button" className="is-danger" onClick={onDeleteSelected}>
            <i className="simple-icon-trash" />
            Xóa
          </button>
        </div>
      )}

    </div>
  );
};

export default StockDashboardMobile;
